import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { Link } from 'react-router-dom';
import {
  daysSince,
  type CreditScoreEntry,
  type Person,
} from '../data/useCreditScores';
import { getPersonName } from '../config/personNames';

// FICO / VantageScore canonical 300–850 range. The arc fill maps linearly
// across this domain so a score of 575 lands at the halfway point.
const MIN_SCORE = 300;
const MAX_SCORE = 850;

// Speedometer-style: 270° arc opening at the bottom. In Recharts angles,
// 0° = 3 o'clock and positive is counter-clockwise. Setting startAngle
// higher than endAngle reverses direction, so 225° → -45° draws clockwise
// through the top, leaving a 90° gap at the bottom.
const START_ANGLE = 225;
const END_ANGLE = -45;
const ARC_SPAN = 270;

const STALE_DAYS = 30;

function rating(score: number): string {
  if (score >= 800) return 'Excellent';
  if (score >= 740) return 'Very good';
  if (score >= 670) return 'Good';
  if (score >= 580) return 'Fair';
  return 'Poor';
}

function fillRatio(score: number): number {
  const clamped = Math.max(MIN_SCORE, Math.min(MAX_SCORE, score));
  return (clamped - MIN_SCORE) / (MAX_SCORE - MIN_SCORE);
}

// Accepts either YYYY-MM-DD or a full ISO timestamp. Returns "today",
// "yesterday", or a short month-day string ("May 31"); appends the year
// only when it differs from the current calendar year.
function relativeOrDate(iso: string): string {
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso;
  const t = Date.parse(normalized);
  if (Number.isNaN(t)) return iso;
  const ageDays = daysSince(iso);
  if (ageDays === 0) return 'today';
  if (ageDays === 1) return 'yesterday';
  const d = new Date(t);
  const sameYear = d.getUTCFullYear() === new Date().getUTCFullYear();
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
    timeZone: 'UTC',
  });
}

interface Props {
  person: Person;
  entries: CreditScoreEntry[];
  /** When true, the per-card Add CTA appears (only on signed-in user's card). */
  isSelf: boolean;
  /** When true, treat the latest entry as stale regardless of age (debug). */
  forceStale: boolean;
  onAddClick: () => void;
  /** If provided, the whole card becomes a Link (dashboard variant). */
  href?: string;
}

export function CreditScoreDonut({
  person,
  entries,
  isSelf,
  forceStale,
  onAddClick,
  href,
}: Props) {
  const latest = entries[0] ?? null;
  const label = getPersonName(person);
  const personColor = `var(--berg-${person})`;
  const trackColor = 'var(--berg-border)';

  const ratio = latest ? fillRatio(latest.score) : 0;
  // Recharts swallows zero-value cells, so seed a hair of fill so the arc
  // geometry stays intact when there's no entry.
  const fillValue = Math.max(ratio, 0.001);
  const data = [
    { name: 'filled', value: fillValue },
    { name: 'unfilled', value: 1 - fillValue },
  ];
  // Per-donut gradient ID — needs to be unique across the page so two
  // donuts on the same screen don't shadow each other's <defs>.
  const gradientId = `credit-fill-${person}`;

  // Linear-gradient axis runs in user space from the start of the arc
  // (always the lower-left at 225°) to the current leading edge. With
  // userSpaceOnUse + these endpoints, the fade behind the leading edge
  // traces an iso-opacity straight line. Coordinates match the chart
  // container (h-44 w-44 → 176x176, outerRadius="100%" → R = 88).
  const CHART_SIZE = 176;
  const CENTER = CHART_SIZE / 2;
  const R = CHART_SIZE / 2;
  const INNER_R = R * 0.78;
  const leadingAngle = START_ANGLE - ratio * ARC_SPAN;
  const leadingRad = (leadingAngle * Math.PI) / 180;
  const toXY = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return { x: CENTER + R * Math.cos(rad), y: CENTER - R * Math.sin(rad) };
  };
  const leadPt = toXY(leadingAngle);
  // Radial cap line: spans exactly the donut thickness at the leading
  // angle (inner radius to outer radius, no overshoot). The gradient
  // peaks at ~50% opacity so this hard line stays the dominant "you are
  // here" marker without poking outside the ring.
  const capInner = {
    x: CENTER + INNER_R * Math.cos(leadingRad),
    y: CENTER - INNER_R * Math.sin(leadingRad),
  };
  const capOuter = {
    x: CENTER + R * Math.cos(leadingRad),
    y: CENTER - R * Math.sin(leadingRad),
  };

  const age = latest ? daysSince(latest.observedAt) : 0;
  const isStale = Boolean(latest) && (forceStale || age > STALE_DAYS);

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    onAddClick();
  };

  // Status row beneath the donut: empty CTA, stale pill + refresh link, or
  // fresh date. Kept a fixed min-height so all three cards align horizontally.
  const status = !latest ? (
    isSelf ? (
      <button type="button" onClick={handleAdd} className="btn btn-outline">
        Add score
      </button>
    ) : (
      <span className="text-xs text-fg-tertiary">No score yet</span>
    )
  ) : isStale ? (
    <>
      <span className="badge bg-overlay text-fg-secondary">
        {age === 0 ? 'Refresh me' : `${age.toLocaleString('en-US')} days ago`}
      </span>
      {isSelf && (
        <button
          type="button"
          onClick={handleAdd}
          className="text-xs font-medium text-fg-secondary hover:text-fg underline underline-offset-2"
        >
          Add fresh
        </button>
      )}
    </>
  ) : (
    <span className="text-xs text-fg-tertiary">
      {latest.model ? `${latest.model} · ` : ''}
      Updated {relativeOrDate(latest.observedAt)}
    </span>
  );

  const inner = (
    <>
      <div className="text-xs uppercase tracking-[0.06em] font-semibold text-fg-tertiary">
        {label}
      </div>
      <div className="relative h-44 w-44">
        <ResponsiveContainer>
          {/* margin=0 forces the donut to fill the container so the cap-line
              overlay's user-space coords (computed off CHART_SIZE) actually
              line up with the rendered arc instead of sitting inside an
              insetted chart area. */}
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <defs>
              {/* Radial gradient centered on the cap: iso-opacity rings are
                  CIRCLES around the cap (not straight lines), so brightness
                  drops with straight-line distance through the donut from
                  the leading edge. Behaves like a physical light source at
                  the cap regardless of how the score distributes the arc. */}
              <radialGradient
                id={gradientId}
                gradientUnits="userSpaceOnUse"
                cx={leadPt.x}
                cy={leadPt.y}
                r={CHART_SIZE * 0.85}
                fx={leadPt.x}
                fy={leadPt.y}
              >
                <stop offset="0%" stopColor={personColor} stopOpacity={0.56} />
                <stop offset="35%" stopColor={personColor} stopOpacity={0.32} />
                <stop offset="75%" stopColor={personColor} stopOpacity={0.12} />
                <stop offset="100%" stopColor={personColor} stopOpacity={0.08} />
              </radialGradient>
            </defs>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              startAngle={START_ANGLE}
              endAngle={END_ANGLE}
              innerRadius="78%"
              outerRadius="100%"
              dataKey="value"
              stroke="none"
              isAnimationActive={false}
            >
              {data.map((d, i) =>
                d.name === 'unfilled' ? (
                  <Cell key={i} fill={trackColor} />
                ) : (
                  <Cell key={i} fill={latest ? `url(#${gradientId})` : trackColor} />
                )
              )}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {/* Hard-edge cap line at the leading position. Overlays the chart
            in its own SVG so we don't have to fight Recharts' children
            schema. Hidden when there's no entry — no leading edge to mark.
            The blurred copy beneath spreads the color outward via
            feGaussianBlur to give the cap a true emanating glow; the
            sharp copy on top keeps the marker crisp. */}
        {latest && (
          <svg
            className="absolute inset-0 pointer-events-none"
            viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <filter
                id={`cap-glow-${person}`}
                x="-100%"
                y="-100%"
                width="300%"
                height="300%"
              >
                <feGaussianBlur stdDeviation="3" />
              </filter>
            </defs>
            <line
              x1={capInner.x}
              y1={capInner.y}
              x2={capOuter.x}
              y2={capOuter.y}
              stroke={personColor}
              strokeOpacity={0.6}
              strokeWidth={6}
              strokeLinecap="butt"
              filter={`url(#cap-glow-${person})`}
            />
            <line
              x1={capInner.x}
              y1={capInner.y}
              x2={capOuter.x}
              y2={capOuter.y}
              stroke={personColor}
              strokeWidth={3}
              strokeLinecap="butt"
            />
          </svg>
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {latest ? (
            <>
              <div className="text-4xl font-bold tabular tracking-tight leading-none">
                {latest.score}
              </div>
              <div
                className="text-xs font-medium mt-1.5"
                style={{ color: personColor }}
              >
                {rating(latest.score)}
              </div>
            </>
          ) : (
            <>
              <div className="text-4xl font-bold text-fg-tertiary tabular tracking-tight leading-none">
                N/A
              </div>
              <div className="text-xs font-medium text-fg-tertiary mt-1.5">
                N/A
              </div>
            </>
          )}
        </div>
      </div>
      <div className="min-h-[28px] flex items-center gap-2 flex-wrap justify-center">
        {status}
      </div>
    </>
  );

  // ARC_SPAN is exported via the leading-comment context; reference it so
  // the unused-symbol linter sees a use if needed.
  void ARC_SPAN;

  const className =
    'card flex flex-col items-center gap-2 text-center transition-colors';

  if (href) {
    return (
      <Link
        to={href}
        className={`${className} no-underline text-inherit hover:border-border-strong`}
        aria-label={`${label}'s credit score history`}
      >
        {inner}
      </Link>
    );
  }
  return <div className={className}>{inner}</div>;
}
